import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Users, CheckCircle, Award, MapPin, Building2, ExternalLink, Filter, Sparkles, Search, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Textarea } from '../components/ui/textarea';

// Constants
const API_TIMEOUT_MS = 60000;
const SCROLL_DELAY_MS = 100;

const FreeZone = () => {
  const [selectedEmirate, setSelectedEmirate] = useState('all');
  const [selectedBusinessType, setSelectedBusinessType] = useState('all');
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiRecommendations, setAiRecommendations] = useState([]);
  const [showRecommendations, setShowRecommendations] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [aiError, setAiError] = useState('');
  const [analysisSummary, setAnalysisSummary] = useState('');

  const benefits = [
    '100% foreign ownership',
    '0% corporate tax for 50 years',
    '0% import and export duties',
    '100% repatriation of capital and profits',
    'No currency restrictions',
    'State-of-the-art infrastructure',
    'Simplified company setup process',
    'Access to world-class facilities'
  ];

  const freeZonesByEmirate = {
    'Abu Dhabi': [
      {
        name: 'Abu Dhabi Media Free Zone',
        specialization: 'Media & Creative Industries',
        description: 'Media Zone Authority offering comprehensive media solutions',
        website: 'https://www.twofour54.com/en/',
        features: ['Content Production', 'Broadcasting', 'Digital Media'],
        startingPrice: 'AED 15,000'
      },
      {
        name: 'Masdar City',
        specialization: 'Clean Energy & Technology',
        description: 'Sustainable city focused on renewable energy and clean technology',
        website: 'https://masdarcity.ae/',
        features: ['Clean Tech', 'Sustainable Development', 'Innovation'],
        startingPrice: 'AED 18,000'
      },
      {
        name: 'Khalifa Industrial Zone Abu Dhabi (KIZAD)',
        specialization: 'Manufacturing & Logistics',
        description: 'Industrial zone supporting manufacturing and logistics operations',
        website: 'https://www.kezadgroup.com/',
        features: ['Manufacturing', 'Logistics', 'Industrial'],
        startingPrice: 'AED 20,000'
      },
      {
        name: 'Abu Dhabi Global Market (ADGM)',
        specialization: 'Financial Services',
        description: 'International financial center with common law framework',
        website: 'https://www.adgm.com/',
        features: ['Banking', 'Insurance', 'Asset Management'],
        startingPrice: 'AED 35,000'
      }
    ],
    'Dubai': [
      {
        name: 'Dubai Multi Commodities Centre (DMCC)',
        specialization: 'Commodities Trading & Multi-Industry',
        description: 'World\'s #1 free zone supporting 600+ business activities',
        website: 'https://dmcc.ae/',
        features: ['Trading', 'Gold & Diamonds', 'Professional Services'],
        popular: true,
        startingPrice: 'AED 10,500'
      },
      {
        name: 'Jebel Ali Free Zone (JAFZA)',
        specialization: 'Trading & Logistics',
        description: 'Middle East\'s largest free zone and logistics hub',
        website: 'https://www.jafza.ae/',
        features: ['Import/Export', 'Warehousing', 'Manufacturing'],
        popular: true,
        startingPrice: 'AED 12,000'
      },
      {
        name: 'Dubai Internet City',
        specialization: 'Technology & IT',
        description: 'IT and telecommunications hub hosting global tech companies',
        website: 'https://dic.ae/',
        features: ['Software', 'IT Services', 'E-commerce'],
        startingPrice: 'AED 13,000'
      },
      {
        name: 'Dubai Media City',
        specialization: 'Media & Broadcasting',
        description: 'Regional media hub for content creation and distribution',
        website: 'https://dmc.ae/',
        features: ['Broadcasting', 'Publishing', 'Advertising'],
        startingPrice: 'AED 12,500'
      },
      {
        name: 'Dubai Silicon Oasis',
        specialization: 'Technology & Electronics',
        description: 'Integrated technology park for electronics and IT',
        website: 'https://www.dso.ae/',
        features: ['Electronics', 'Software Development', 'R&D'],
        startingPrice: 'AED 11,500'
      },
      {
        name: 'Dubai International Financial Centre (DIFC)',
        specialization: 'Financial Services',
        description: 'Leading financial hub connecting East and West markets',
        website: 'https://www.difc.ae/',
        features: ['Banking', 'Wealth Management', 'Capital Markets'],
        popular: true,
        startingPrice: 'AED 25,000'
      },
      {
        name: 'Dubai Healthcare City (DHCC)',
        specialization: 'Healthcare & Medical',
        description: 'Healthcare free zone for medical facilities and services',
        website: 'https://dhcc.ae/',
        features: ['Hospitals', 'Clinics', 'Medical Research'],
        startingPrice: 'AED 16,000'
      },
      {
        name: 'Dubai Airport Free Zone (DAFZ)',
        specialization: 'Aviation & Logistics',
        description: 'Strategic location for aviation and logistics businesses',
        website: 'https://www.dafz.ae/en/',
        features: ['Aviation', 'Cargo', 'Logistics'],
        startingPrice: 'AED 14,000'
      },
      {
        name: 'Dubai Production City',
        specialization: 'Production & Manufacturing',
        description: 'Specialized zone for media and manufacturing production',
        website: 'https://dpc.ae/',
        features: ['Film Production', 'Manufacturing', 'Printing'],
        startingPrice: 'AED 12,000'
      },
      {
        name: 'Dubai Studio City',
        specialization: 'Film & TV Production',
        description: 'Dedicated to film, TV, and music production',
        website: 'https://dubaistudiocity.ae/',
        features: ['Film Studios', 'Post Production', 'Sound Stages'],
        startingPrice: 'AED 13,500'
      },
      {
        name: 'Dubai Knowledge Park',
        specialization: 'Education & Training',
        description: 'Hub for education providers and training centers',
        website: 'https://dkp.ae/',
        features: ['Universities', 'Training', 'E-learning'],
        startingPrice: 'AED 12,000'
      },
      {
        name: 'Dubai Science Park',
        specialization: 'Science & Research',
        description: 'Innovation hub for scientific and research activities',
        website: 'https://dsp.ae/',
        features: ['Biotech', 'Pharma', 'Research'],
        startingPrice: 'AED 14,500'
      },
      {
        name: 'Dubai Design District (d3)',
        specialization: 'Design & Fashion',
        description: 'Creative hub for design, fashion, and luxury brands',
        website: 'https://dubaidesigndistrict.com/',
        features: ['Fashion', 'Design', 'Luxury Retail'],
        startingPrice: 'AED 15,000'
      },
      {
        name: 'Dubai CommerCity (DCC)',
        specialization: 'E-commerce',
        description: 'First e-commerce focused free zone',
        website: 'https://www.dubaicommercity.ae/',
        features: ['E-commerce', 'Online Retail', 'Logistics'],
        startingPrice: 'AED 11,000'
      },
      {
        name: 'Dubai South',
        specialization: 'Aviation & Logistics',
        description: 'Connected to Al Maktoum International Airport',
        website: 'https://www.dubaisouth.ae/en',
        features: ['Aviation', 'Logistics', 'Residential'],
        startingPrice: 'AED 13,500'
      },
      {
        name: 'International Humanitarian City',
        specialization: 'Humanitarian & NGO',
        description: 'Global hub for humanitarian organizations',
        website: 'https://dubaihumanitarian.ae/',
        features: ['NGOs', 'Humanitarian Aid', 'Relief Services'],
        startingPrice: 'AED 10,000'
      },
      {
        name: 'Dubai Maritime City',
        specialization: 'Maritime & Shipping',
        description: 'Integrated maritime center',
        website: 'https://dubaimaritimecity.com/',
        features: ['Shipping', 'Maritime Services', 'Port Operations'],
        startingPrice: 'AED 15,500'
      },
      {
        name: 'Meydan Free Zone',
        specialization: 'General Trading',
        description: 'Multi-purpose free zone with flexible licensing',
        website: 'https://www.meydanfz.ae/',
        features: ['Trading', 'Services', 'Manufacturing'],
        startingPrice: 'AED 12,500'
      },
      {
        name: 'International Free Zone Authority (IFZA)',
        specialization: 'Multi-Industry',
        description: 'Flexible licensing for various business activities',
        website: 'https://ifza.com/en/',
        features: ['Trading', 'Consultancy', 'E-commerce'],
        startingPrice: 'AED 6,750'
      }
    ],
    'Sharjah': [
      {
        name: 'Sharjah Airport International Free Zone (SAIF)',
        specialization: 'Aviation & Trading',
        description: 'Cost-effective free zone near Sharjah Airport',
        website: 'https://www.saif-zone.com/',
        features: ['Trading', 'Logistics', 'Manufacturing'],
        startingPrice: 'AED 8,500'
      },
      {
        name: 'Hamriyah Free Zone',
        specialization: 'Industrial & Manufacturing',
        description: 'Industrial free zone with port facilities',
        website: 'https://hfza.ae/',
        features: ['Manufacturing', 'Heavy Industry', 'Petrochemicals'],
        startingPrice: 'AED 10,500'
      },
      {
        name: 'Sharjah Media City (Shams)',
        specialization: 'Media & Freelance',
        description: 'Free zone for media professionals and freelancers',
        website: 'https://www.shams.ae/',
        features: ['Media', 'Freelancing', 'Creative Services'],
        startingPrice: 'AED 7,500'
      }
    ],
    'Ras Al Khaimah': [
      {
        name: 'Ras Al Khaimah Economic Zone (RAKEZ)',
        specialization: 'Multi-Industry',
        description: 'Cost-effective zone supporting various industries',
        website: 'https://rakez.com/en/',
        features: ['Manufacturing', 'Trading', 'Services'],
        startingPrice: 'AED 6,500'
      },
      {
        name: 'RAK Digital Assets Oasis',
        specialization: 'Digital Assets & Crypto',
        description: 'Specialized zone for digital assets and blockchain',
        website: 'https://www.rakdao.com/',
        features: ['Cryptocurrency', 'Blockchain', 'Digital Assets'],
        startingPrice: 'AED 12,000'
      }
    ],
    'Ajman': [
      {
        name: 'Ajman Free Zone',
        specialization: 'Trading & Services',
        description: 'Affordable free zone for SMEs',
        website: 'https://afz.ae/',
        features: ['Trading', 'Services', 'Manufacturing'],
        startingPrice: 'AED 5,750'
      }
    ],
    'Umm Al Quwain': [
      {
        name: 'Umm Al Quwain Free Trade Zone',
        specialization: 'Trading & Manufacturing',
        description: 'Budget-friendly free zone option',
        website: 'https://uaqftz.com/',
        features: ['Trading', 'Light Manufacturing', 'Storage'],
        startingPrice: 'AED 6,000'
      }
    ],
    'Fujairah': [
      {
        name: 'Fujairah Free Zone',
        specialization: 'Trading & Services',
        description: 'Strategic location on UAE east coast',
        website: 'https://www.fujairahfreezone.com/',
        features: ['Trading', 'Import/Export', 'Services'],
        startingPrice: 'AED 7,500'
      },
      {
        name: 'Creative City Fujairah',
        specialization: 'Media & Creative',
        description: 'Hub for media and creative professionals',
        website: 'https://creativecity.gov.ae/',
        features: ['Media', 'Publishing', 'Creative Services'],
        startingPrice: 'AED 8,000'
      }
    ]
  };

  const emirates = ['all', ...Object.keys(freeZonesByEmirate)];

  const businessTypes = [
    { value: 'all', label: 'All Types', keywords: [] },
    { value: 'trading', label: 'Trading & Commerce', keywords: ['trading', 'import', 'export', 'commerce', 'buy', 'sell', 'wholesale', 'retail', 'distribution'] },
    { value: 'services', label: 'Professional Services', keywords: ['consulting', 'consultancy', 'advisory', 'service', 'professional', 'legal', 'accounting', 'audit'] },
    { value: 'technology', label: 'Technology & IT', keywords: ['technology', 'software', 'it', 'tech', 'digital', 'app', 'website', 'saas', 'cloud', 'ai', 'data', 'cyber'] },
    { value: 'industrial', label: 'Manufacturing & Industrial', keywords: ['manufacturing', 'factory', 'production', 'industrial', 'assembly', 'fabrication'] },
    { value: 'media', label: 'Media & Creative', keywords: ['media', 'advertising', 'marketing', 'creative', 'design', 'content', 'production', 'film', 'video', 'photography'] },
    { value: 'financial', label: 'Financial Services', keywords: ['finance', 'financial', 'banking', 'investment', 'wealth', 'insurance', 'fintech', 'capital'] },
    { value: 'healthcare', label: 'Healthcare & Medical', keywords: ['healthcare', 'medical', 'health', 'clinic', 'hospital', 'pharma', 'pharmaceutical', 'biotech'] },
    { value: 'education', label: 'Education & Training', keywords: ['education', 'training', 'teaching', 'learning', 'university', 'school', 'academy', 'course'] },
    { value: 'logistics', label: 'Logistics & Transportation', keywords: ['logistics', 'transport', 'shipping', 'cargo', 'freight', 'warehouse', 'supply chain'] },
    { value: 'ecommerce', label: 'E-commerce & Online', keywords: ['ecommerce', 'e-commerce', 'online', 'marketplace', 'dropshipping', 'online store'] },
    { value: 'food', label: 'Food & Beverage', keywords: ['food', 'restaurant', 'cafe', 'catering', 'beverage', 'culinary'] },
    { value: 'travel', label: 'Travel & Tourism', keywords: ['travel', 'tourism', 'tour', 'hotel', 'hospitality', 'resort'] }
  ];

  const analyzeBusinessPrompt = async (prompt) => {
    setIsAnalyzing(true);
    setAiError('');
    setAnalysisSummary('');
    
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), API_TIMEOUT_MS);
      
      const response = await fetch(`${API_URL}/api/freezone-finder`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ business_description: prompt }),
        signal: controller.signal,
      });
      
      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error('Failed to get AI recommendations');
      }

      const data = await response.json();
      
      // Transform API response to match existing UI format
      const recommendations = data.recommendations.map((rec) => ({
        zone: {
          name: rec.name,
          emirate: rec.emirate,
          specialization: rec.specialization,
          description: rec.description,
          features: rec.features,
          website: rec.website,
          popular: rec.match_score >= 90,
        },
        score: rec.match_score,
        reasons: rec.match_reasons,
      }));

      setAiRecommendations(recommendations);
      setAnalysisSummary(data.analysis_summary || '');
      setShowRecommendations(true);
      
      // Auto-scroll to recommendations after a short delay
      setTimeout(() => {
        document.getElementById('ai-recommendations')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, SCROLL_DELAY_MS);
    } catch (error) {
      if (error.name === 'AbortError') {
        setAiError('Request timed out. Please try again.');
      } else {
        setAiError('Unable to get AI recommendations. Please try again.');
      }
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handleAiSearch = () => {
    if (aiPrompt.trim()) {
      analyzeBusinessPrompt(aiPrompt);
    }
  };

  const clearRecommendations = () => {
    setShowRecommendations(false);
    setAiRecommendations([]);
    setAiPrompt('');
    setAiError('');
    setAnalysisSummary('');
  };

  const getFilteredZones = () => {
    let zones = [];
    
    if (selectedEmirate === 'all') {
      Object.entries(freeZonesByEmirate).forEach(([emirate, freeZones]) => {
        zones.push(...freeZones.map(zone => ({ ...zone, emirate })));
      });
    } else {
      zones = freeZonesByEmirate[selectedEmirate].map(zone => ({
        ...zone,
        emirate: selectedEmirate
      }));
    }

    // Filter by business type
    if (selectedBusinessType !== 'all') {
      const businessType = businessTypes.find(bt => bt.value === selectedBusinessType);
      zones = zones.filter(zone => {
        const zoneText = `${zone.name} ${zone.specialization} ${zone.features.join(' ')}`.toLowerCase();
        return businessType.keywords.some(keyword => zoneText.includes(keyword));
      });
    }

    return zones;
  };

  const filteredZones = showRecommendations ? [] : getFilteredZones();

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section 
        className="relative py-32 flex items-center"
        style={{
          backgroundImage: 'linear-gradient(rgba(107, 27, 77, 0.8), rgba(45, 27, 61, 0.8)), url(https://images.unsplash.com/photo-1512453979798-5ea266f8880c?w=1920&q=80)',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="container mx-auto px-4">
          <div className="max-w-3xl text-white">
            <div className="flex items-center gap-3 mb-4">
              <Users size={48} className="text-[#DC143C]" />
              <span className="text-2xl font-bold">50+ Free Zones Across UAE</span>
            </div>
            <h1 className="text-5xl font-bold mb-6">UAE Free Zone Company Formation</h1>
            <p className="text-xl leading-relaxed mb-8">
              Explore over 50 free zones across all seven emirates. Each zone offers unique benefits tailored to specific industries and business needs.
            </p>
            <Button 
              size="lg" 
              className="bg-[#DC143C] hover:bg-[#DC143C]/90 text-white px-8 py-6 text-lg"
              onClick={() => {
                document.getElementById('ai-finder')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
              }}
            >
              <Sparkles className="mr-2" size={20} />
              Find Your Perfect Free Zone with AI
            </Button>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold text-gray-800 mb-12 text-center">Why Choose Free Zone?</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
            {benefits.map((benefit, idx) => (
              <div key={idx} className="bg-white p-6 rounded-xl shadow-md hover:shadow-xl transition-all">
                <CheckCircle className="text-green-600 mb-3" size={32} />
                <p className="text-gray-800 font-medium">{benefit}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* AI Business Matcher */}
      <section id="ai-finder" className="py-12 bg-gradient-to-br from-purple-50 to-pink-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-6">
              <div className="inline-flex items-center gap-2 bg-gradient-to-r from-[#6B1B4D] to-[#DC143C] text-white px-4 py-2 rounded-full mb-4">
                <Sparkles size={20} />
                <span className="font-bold">AI-Powered Free Zone Finder</span>
              </div>
              <h2 className="text-3xl font-bold text-gray-800 mb-3">
                Describe Your Business, Get Perfect Matches
              </h2>
              <p className="text-gray-600">
                Tell us about your business in your own words, and our AI will recommend the best free zones for you
              </p>
            </div>

            <div className="bg-white rounded-2xl shadow-xl p-6 border-2 border-purple-200">
              <div className="mb-4">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Describe your business activity:
                </label>
                <Textarea
                  data-testid="ai-finder-textarea"
                  placeholder="Example: I want to start an e-commerce business selling electronics online to customers in UAE and internationally..."
                  value={aiPrompt}
                  onChange={(e) => setAiPrompt(e.target.value)}
                  className="min-h-32 text-base"
                  disabled={isAnalyzing}
                />
              </div>
              <Button
                data-testid="ai-finder-submit-button"
                onClick={handleAiSearch}
                disabled={!aiPrompt.trim() || isAnalyzing}
                className="w-full bg-gradient-to-r from-[#6B1B4D] to-[#DC143C] text-white hover:shadow-lg text-lg py-6"
              >
                {isAnalyzing ? (
                  <>
                    <Sparkles className="mr-2 animate-spin" size={20} />
                    Analyzing Your Business...
                  </>
                ) : (
                  <>
                    <Search className="mr-2" size={20} />
                    Find Perfect Free Zones for My Business
                  </>
                )}
              </Button>
              
              {/* Error Message */}
              {aiError && (
                <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 flex items-center gap-2" data-testid="ai-error-message">
                  <X size={20} className="text-red-500" />
                  <span>{aiError}</span>
                  <button 
                    onClick={() => setAiError('')}
                    className="ml-auto text-red-500 hover:text-red-700"
                  >
                    <X size={16} />
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* AI Recommendations Display */}
      {showRecommendations && aiRecommendations.length > 0 && (
        <section id="ai-recommendations" className="py-12 bg-white" data-testid="ai-recommendations-section">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <div className="flex justify-between items-center mb-4">
                <div>
                  <h2 className="text-3xl font-bold text-gray-800 mb-2" data-testid="ai-recommendations-heading">
                    <Sparkles className="inline text-purple-600 mr-2" size={32} />
                    Top 3 Recommended Free Zones
                  </h2>
                  <p className="text-gray-600">Based on your business requirements</p>
                </div>
                <Button
                  onClick={clearRecommendations}
                  variant="outline"
                  className="border-gray-300"
                >
                  <X size={16} className="mr-2" />
                  Clear & Browse All
                </Button>
              </div>
              
              {/* Analysis Summary */}
              {analysisSummary && (
                <div className="mb-6 p-4 bg-gradient-to-r from-purple-50 to-pink-50 border border-purple-200 rounded-lg" data-testid="ai-analysis-summary">
                  <p className="text-gray-700 italic">&ldquo;{analysisSummary}&rdquo;</p>
                </div>
              )}

              <div className="grid md:grid-cols-3 gap-6 mb-8">
                {aiRecommendations.map((rec, idx) => {
                  const zone = rec.zone;
                  return (
                    <Card key={idx} data-testid={`ai-recommendation-card-${idx}`} className="overflow-hidden border-4 border-purple-300 hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 relative">
                      {/* Rank Badge */}
                      <div className="absolute top-4 right-4 w-12 h-12 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-full flex items-center justify-center text-white font-bold text-xl shadow-lg z-10">
                        #{idx + 1}
                      </div>

                      <div className="p-6 bg-gradient-to-r from-purple-600 to-pink-600 text-white">
                        <MapPin size={24} className="mb-2" />
                        <h3 className="text-xl font-bold mb-2" data-testid={`ai-rec-name-${idx}`}>{zone.name}</h3>
                        <p className="text-sm opacity-90" data-testid={`ai-rec-emirate-${idx}`}>{zone.emirate}</p>
                      </div>

                      <div className="p-6">
                        <div className="mb-4">
                          <div className="inline-block bg-purple-50 text-purple-700 text-sm font-semibold px-3 py-1 rounded-full mb-3">
                            {zone.specialization}
                          </div>
                          <p className="text-gray-600 text-sm leading-relaxed mb-3">
                            {zone.description}
                          </p>
                        </div>

                        {/* Match Reasons */}
                        <div className="mb-4 bg-green-50 border border-green-200 rounded-lg p-3">
                          <h4 className="font-bold text-green-800 mb-2 text-sm flex items-center gap-1">
                            <CheckCircle size={16} />
                            Why This Matches:
                          </h4>
                          <ul className="space-y-1" data-testid={`ai-rec-reasons-${idx}`}>
                            {rec.reasons.map((reason, rIdx) => (
                              <li key={rIdx} className="text-xs text-green-700 flex items-start gap-1">
                                <span className="text-green-500">✓</span>
                                {reason}
                              </li>
                            ))}
                          </ul>
                        </div>

                        <div className="flex gap-2">
                          <a 
                            href={zone.website}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex-1"
                          >
                            <Button 
                              variant="outline" 
                              className="w-full border-purple-600 text-purple-600 hover:bg-purple-600 hover:text-white"
                              size="sm"
                            >
                              <ExternalLink size={14} className="mr-1" />
                              Visit
                            </Button>
                          </a>
                          <Link to="/contact" className="flex-1">
                            <Button 
                              className="w-full bg-gradient-to-r from-purple-600 to-pink-600 text-white"
                              size="sm"
                            >
                              Get Quote
                            </Button>
                          </Link>
                        </div>
                      </div>
                    </Card>
                  );
                })}
              </div>

              <div className="text-center">
                <p className="text-gray-600 mb-4">Want to see all options?</p>
                <Button
                  onClick={clearRecommendations}
                  variant="outline"
                  className="border-[#6B1B4D] text-[#6B1B4D] hover:bg-[#6B1B4D] hover:text-white"
                >
                  Browse All {Object.values(freeZonesByEmirate).flat().length}+ Free Zones
                </Button>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Filter Section */}
      {!showRecommendations && (
        <section className="py-8 bg-white border-b">
          <div className="container mx-auto px-4">
            {/* Business Type Filter */}
            <div className="mb-6">
              <div className="flex items-center gap-2 mb-3">
                <Building2 size={20} className="text-gray-600" />
                <span className="font-semibold text-gray-700">Filter by Business Type:</span>
              </div>
              <div className="flex flex-wrap gap-2">
                {businessTypes.map((type) => (
                  <button
                    key={type.value}
                    onClick={() => {
                      setSelectedBusinessType(type.value);
                      setSelectedEmirate('all');
                    }}
                    className={`px-4 py-2 rounded-lg transition-all text-sm ${
                      selectedBusinessType === type.value
                        ? 'bg-gradient-to-r from-blue-600 to-cyan-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {type.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Emirate Filter */}
            <div>
              <div className="flex items-center gap-2 mb-3">
                <Filter size={20} className="text-gray-600" />
                <span className="font-semibold text-gray-700">Filter by Emirate:</span>
              </div>
              <div className="flex flex-wrap gap-2">
                {emirates.map((emirate) => (
                  <button
                    key={emirate}
                    onClick={() => setSelectedEmirate(emirate)}
                    className={`px-4 py-2 rounded-lg transition-all ${
                      selectedEmirate === emirate
                        ? 'bg-gradient-to-r from-[#6B1B4D] to-[#DC143C] text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {emirate === 'all' ? 'All Emirates' : emirate}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Free Zones Listing */}
      {!showRecommendations && (
        <section className="py-20 bg-white">
          <div className="container mx-auto px-4">
            <div className="mb-8">
              <h2 className="text-3xl font-bold text-gray-800">
                {selectedBusinessType !== 'all' 
                  ? `${businessTypes.find(bt => bt.value === selectedBusinessType)?.label} Free Zones (${filteredZones.length})`
                  : selectedEmirate === 'all' 
                    ? `All Free Zones (${filteredZones.length})`
                    : `${selectedEmirate} Free Zones (${filteredZones.length})`
                }
              </h2>
            </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredZones.map((zone, idx) => (
              <Card key={idx} className="overflow-hidden hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 border-2 border-gray-100 hover:border-[#6B1B4D]">
                <div className={`p-6 ${zone.popular ? 'bg-gradient-to-r from-[#6B1B4D] to-[#DC143C]' : 'bg-gradient-to-r from-gray-700 to-gray-800'} text-white`}>
                  <div className="flex items-center justify-between mb-2">
                    <MapPin size={24} />
                    {zone.popular && (
                      <span className="bg-yellow-400 text-gray-900 text-xs font-bold px-2 py-1 rounded">
                        POPULAR
                      </span>
                    )}
                  </div>
                  <h3 className="text-xl font-bold mb-2">{zone.name}</h3>
                  <p className="text-sm opacity-90">{zone.emirate}</p>
                </div>

                <div className="p-6">
                  <div className="mb-4">
                    <div className="inline-block bg-blue-50 text-blue-700 text-sm font-semibold px-3 py-1 rounded-full mb-3">
                      {zone.specialization}
                    </div>
                    <p className="text-gray-600 text-sm leading-relaxed">
                      {zone.description}
                    </p>
                  </div>

                  <div className="mb-4">
                    <h4 className="font-semibold text-gray-800 mb-2 text-sm">Key Features:</h4>
                    <div className="flex flex-wrap gap-2">
                      {zone.features.map((feature, fIdx) => (
                        <span key={fIdx} className="bg-gray-100 text-gray-700 text-xs px-2 py-1 rounded">
                          {feature}
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Pricing */}
                  <div className="mb-4 bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg p-3">
                    <p className="text-xs text-gray-600 mb-1">Starting from</p>
                    <p className="text-2xl font-bold text-green-700">{zone.startingPrice}</p>
                    <p className="text-xs text-gray-500 mt-1">*Pricing may vary based on license type</p>
                  </div>

                  <div className="flex gap-2">
                    <a 
                      href={zone.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex-1"
                    >
                      <Button 
                        variant="outline" 
                        className="w-full border-[#6B1B4D] text-[#6B1B4D] hover:bg-[#6B1B4D] hover:text-white"
                        size="sm"
                      >
                        <ExternalLink size={14} className="mr-1" />
                        Visit Website
                      </Button>
                    </a>
                    <Link to="/contact" className="flex-1">
                      <Button 
                        className="w-full bg-gradient-to-r from-[#6B1B4D] to-[#DC143C] text-white"
                        size="sm"
                      >
                        Get Quote
                      </Button>
                    </Link>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>
      )}

      {/* CTA */}
      <section className="py-20 bg-gradient-to-r from-[#6B1B4D] to-[#DC143C] text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-6">Not Sure Which Free Zone is Right for You?</h2>
          <p className="text-xl mb-8">Our experts will help you choose the perfect free zone for your business based on your industry and requirements</p>
          <Link to="/contact">
            <Button 
              size="lg" 
              className="bg-white text-[#6B1B4D] hover:bg-gray-100 px-8 py-6 text-lg"
            >
              Get Free Consultation
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default FreeZone;
